'use client';

import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  FilterOption,
  PathStep,
} from '@/util/types/serverTypeCollection/serviceFilter';
import { FaArrowLeft, FaChevronRight } from 'react-icons/fa6';
import { Button, Breadcrumbs, BreadcrumbItem } from '@heroui/react';
import { motion } from 'framer-motion';
import { useScheduler } from '../SchedulerContext';
import ServiceSearchActions from './ServiceSearchActions';

type ServiceFilterFormProps = {
  onCompleteAction: (result: {
    serviceIds: string[];
    roleIds: string[];
  }) => void;
  isDisabled?: boolean;
  onSearch?: () => void;
  path?: PathStep[];
  setPath?: React.Dispatch<React.SetStateAction<PathStep[]>>;
};

export default function ServiceFilterForm({
  onCompleteAction,
  isDisabled = false,
  onSearch,
  path: externalPath,
  setPath: externalSetPath,
}: ServiceFilterFormProps) {
  const { serviceFilter, companyStyles, scheduleSettings } = useScheduler();

  // Use external path state if provided, otherwise use internal state
  const [internalPath, setInternalPath] = useState<PathStep[]>([]);
  const path = externalPath !== undefined ? externalPath : internalPath;
  const setPath = externalSetPath || setInternalPath;

  // Filter out orphaned options (branches that don't lead to a terminal node) and inactive branches
  const hasTerminalDescendant = (node: FilterOption): boolean => {
    // If node is inactive, exclude it and all its children
    if (node.active === false) {
      return false;
    }

    // If this node is terminal, return true
    if (
      node.accumulatedServiceIds &&
      node.accumulatedServiceIds.length > 0 &&
      node.accumulatedRoleIds &&
      node.accumulatedRoleIds.length > 0
    ) {
      return true;
    }

    // If it has no children, it's a leaf node without being terminal (orphaned)
    if (!node.subOptions || node.subOptions.length === 0) {
      return false;
    }

    // Check if any child has a terminal descendant
    return node.subOptions.some((child) => hasTerminalDescendant(child));
  };

  const filterValidOptions = (options: FilterOption[]): FilterOption[] => {
    return options
      .filter((opt) => opt.active !== false && hasTerminalDescendant(opt))
      .map((opt) => ({
        ...opt,
        subOptions: opt.subOptions
          ? filterValidOptions(opt.subOptions)
          : undefined,
      }));
  };

  const validRootOptions = useMemo(
    () =>
      filterValidOptions(serviceFilter.options.filter((opt) => !opt.parentId)),
    [serviceFilter.options]
  );

  // Initialize currentOptions based on path if provided
  const getOptionsFromPath = (pathToUse: PathStep[]): FilterOption[] => {
    if (pathToUse.length === 0) {
      return validRootOptions;
    }

    let options: FilterOption[] = validRootOptions;
    let lastValidIndex = -1;

    // Find the last step that has sub-options (non-terminal)
    for (let i = 0; i < pathToUse.length; i++) {
      const step = pathToUse[i];
      const option = options.find((opt) => opt._id === step.optionId);
      if (!option) {
        // Invalid path, return root
        return validRootOptions;
      }

      if (option.subOptions && option.subOptions.length > 0) {
        options = option.subOptions;
        lastValidIndex = i;
      } else {
        // This is a terminal node - stop here
        break;
      }
    }

    // If we found a valid non-terminal step, return its options
    if (lastValidIndex >= 0) {
      // Re-traverse to get the correct options
      options = validRootOptions;
      for (let i = 0; i <= lastValidIndex; i++) {
        const step = pathToUse[i];
        const option = options.find((opt) => opt._id === step.optionId);
        if (option?.subOptions) {
          options = option.subOptions;
        }
      }
      return options;
    }

    // Path only contains terminal nodes or is invalid, return root
    return validRootOptions;
  };

  const [currentOptions, setCurrentOptions] =
    useState<FilterOption[]>(validRootOptions);
  const lastProcessedPathRef = useRef<string>('');
  const lastOptionsRef = useRef<FilterOption[]>(validRootOptions);

  // Restore currentOptions when path changes (e.g., when navigating back)
  useEffect(() => {
    // Create a stable key from the path to avoid processing the same path multiple times
    const pathKey = JSON.stringify(path.map((p) => p.optionId));

    // Skip if we've already processed this exact path
    if (lastProcessedPathRef.current === pathKey) {
      return;
    }

    const newOptions = getOptionsFromPath(path);

    // Only update if the options actually changed (compare by IDs)
    const currentOptionIds = JSON.stringify(
      lastOptionsRef.current.map((opt) => opt._id)
    );
    const newOptionIds = JSON.stringify(newOptions.map((opt) => opt._id));

    if (currentOptionIds !== newOptionIds) {
      setCurrentOptions(newOptions);
      lastOptionsRef.current = newOptions;
    }

    // Trim path to remove terminal nodes if needed
    if (path.length > 0 && setPath) {
      let options: FilterOption[] = validRootOptions;
      let lastValidIndex = -1;

      for (let i = 0; i < path.length; i++) {
        const step = path[i];
        const option = options.find((opt) => opt._id === step.optionId);
        if (option?.subOptions && option.subOptions.length > 0) {
          options = option.subOptions;
          lastValidIndex = i;
        } else {
          break;
        }
      }

      // If path has terminal nodes, trim them
      if (lastValidIndex >= 0 && lastValidIndex < path.length - 1) {
        const trimmedPath = path.slice(0, lastValidIndex + 1);
        const trimmedPathKey = JSON.stringify(
          trimmedPath.map((p) => p.optionId)
        );

        // Only update if the trimmed path is different from current path
        if (trimmedPathKey !== pathKey) {
          lastProcessedPathRef.current = trimmedPathKey;
          setPath(trimmedPath);
          return; // Exit early to avoid processing the new path in this same effect
        }
      }
    }

    // Mark this path as processed
    lastProcessedPathRef.current = pathKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, validRootOptions]); // setPath is a stable setter function, doesn't need to be in deps

  const handleOptionClick = (option: FilterOption) => {
    // Add current selection to path
    const newPathStep: PathStep = {
      optionId: option._id,
      selectedLabel: option.label,
    };

    const newPath = [...path, newPathStep];
    setPath(newPath);

    // Check if this option has subOptions (non-terminal node)
    if (option.subOptions && option.subOptions.length > 0) {
      setCurrentOptions(option.subOptions);
      return;
    }

    // Terminal node - complete the selection
    onCompleteAction({
      serviceIds: option.accumulatedServiceIds || [],
      roleIds: option.accumulatedRoleIds || [],
    });
  };

  const navigateToStep = (stepIndex: number) => {
    // Slice path to the selected step
    const newPath = path.slice(0, stepIndex);
    setPath(newPath);

    if (newPath.length === 0) {
      // Back to root
      setCurrentOptions(validRootOptions);
    } else {
      // Traverse down to the correct level
      let options: FilterOption[] = validRootOptions;
      for (const step of newPath) {
        const option = options.find((opt) => opt._id === step.optionId);
        if (option?.subOptions) {
          options = option.subOptions;
        }
      }
      setCurrentOptions(options);
    }
  };

  const handleBack = () => {
    if (path.length === 0) return;
    navigateToStep(path.length - 1);
  };

  const handleReset = () => {
    setPath([]);
    setCurrentOptions(validRootOptions);
  };

  // Get current question based on path
  const getCurrentQuestion = () => {
    if (path.length === 0) {
      // Root level
      return 'What can we help you with?';
    }

    // Find parent option (last item in path)
    const parentOptionId = path[path.length - 1].optionId;
    const findOption = (
      options: FilterOption[],
      targetId: string
    ): FilterOption | null => {
      for (const opt of options) {
        if (opt._id === targetId) return opt;
        if (opt.subOptions) {
          const found = findOption(opt.subOptions, targetId);
          if (found) return found;
        }
      }
      return null;
    };

    const parentOption = findOption(validRootOptions, parentOptionId);
    return parentOption?.question || 'Choose from the following options';
  };

  return (
    <div className='space-y-6'>
      {/* Breadcrumb Navigation */}
      {path.length > 0 && (
        <div
          style={companyStyles.cssVars}
          className='flex items-center justify-between gap-4'
        >
          <Breadcrumbs
            size='md'
            classNames={{ list: 'text-[var(--company-secondary)]' }}
          >
            <BreadcrumbItem key='root' onPress={handleReset}>
              Home
            </BreadcrumbItem>
            {path.map((step, index) => (
              <BreadcrumbItem
                key={`step-${index}`}
                onPress={() => navigateToStep(index + 1)}
                isCurrent={index === path.length - 1}
              >
                {step.selectedLabel}
              </BreadcrumbItem>
            ))}
          </Breadcrumbs>
          {scheduleSettings?.showPricingInOnlineScheduler === false && (
            <span className='text-sm text-gray-500'>Price TBD</span>
          )}
        </div>
      )}

      {/* Question */}
      <div>
        <h2 className='text-2xl font-bold text-gray-900'>
          {getCurrentQuestion()}
        </h2>
      </div>

      {/* Options Grid */}
      <div
        className='grid gap-3 sm:grid-cols-2 sm:gap-4'
        style={companyStyles.cssVars}
      >
        {currentOptions.map((option, index) => (
          <motion.button
            key={option._id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.4,
              delay: index * 0.1,
              ease: [0.22, 1, 0.36, 1],
            }}
            onClick={() => !isDisabled && handleOptionClick(option)}
            disabled={isDisabled}
            className={`group relative flex items-center gap-2 overflow-hidden rounded-xl border-2 p-4 text-left sm:p-6 ${
              isDisabled
                ? 'cursor-not-allowed border-gray-100 bg-gray-50 opacity-60'
                : 'border-gray-200 bg-white hover:shadow-lg'
            }`}
            style={
              !isDisabled
                ? {
                    borderColor: 'rgb(229, 231, 235)',
                  }
                : undefined
            }
            onMouseEnter={(e) => {
              if (!isDisabled) {
                e.currentTarget.style.borderColor =
                  companyStyles.companyColor || '#006FEE';
              }
            }}
            onMouseLeave={(e) => {
              if (!isDisabled) {
                e.currentTarget.style.borderColor = 'rgb(229, 231, 235)';
              }
            }}
          >
            <div className='h-full flex-1 space-y-1 sm:space-y-2'>
              <h3
                className={`text-base font-semibold transition-colors sm:text-lg ${
                  isDisabled
                    ? 'text-gray-400'
                    : 'text-gray-900 group-hover:text-(--company-secondary)'
                }`}
                style={
                  !isDisabled
                    ? ({
                        '--company-secondary':
                          companyStyles.companyColor || '#006FEE',
                      } as React.CSSProperties)
                    : undefined
                }
              >
                {option.label}
              </h3>
              {option.description && (
                <p
                  className={`text-sm ${
                    isDisabled ? 'text-gray-400' : 'text-gray-600'
                  }`}
                >
                  {option.description}
                </p>
              )}
            </div>
            <div
              className={`ml-4 shrink-0 transition-all ${
                isDisabled
                  ? 'text-gray-300'
                  : 'text-gray-400 group-hover:translate-x-1 group-hover:text-(--company-secondary)'
              }`}
              style={
                !isDisabled
                  ? ({
                      '--company-secondary':
                        companyStyles.companyColor || '#006FEE',
                    } as React.CSSProperties)
                  : undefined
              }
            >
              <FaChevronRight size={20} />
            </div>
          </motion.button>
        ))}
      </div>

      {/* Back Button */}
      {path.length > 0 && !isDisabled && (
        <div className='flex items-center justify-between pt-4'>
          <Button
            onPress={handleBack}
            variant='flat'
            startContent={<FaArrowLeft />}
            color='default'
          >
            Back
          </Button>
        </div>
      )}

      {/* Start Over Section */}
      {!isDisabled && (
        <ServiceSearchActions
          onStartOver={path.length > 0 ? handleReset : undefined}
          onSearch={onSearch}
        />
      )}
    </div>
  );
}
